import React, { useState, useCallback } from 'react';
import { Card } from '@/components/ui/card';
import { Copy, Check, Download, QrCode, ExternalLink } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export interface QRCodeGeneratorCardProps {
    text: string;
    qrUrl: string;
    isDarkMode: boolean;
}

export const QRCodeGeneratorCard: React.FC<QRCodeGeneratorCardProps> = ({
    text,
    qrUrl,
    isDarkMode
}) => {
    const { toast } = useToast();
    const [copied, setCopied] = useState(false);
    const [imageLoaded, setImageLoaded] = useState(false);

    const handleCopyText = useCallback(async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            toast({ title: 'Copied!' });
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            toast({ title: 'Failed to copy', variant: 'destructive' });
        }
    }, [text, toast]);

    const handleDownload = useCallback(async () => {
        try {
            // Fetch the QR code image as blob
            const response = await fetch(qrUrl);
            const blob = await response.blob();

            // Create a download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `qr-code-${Date.now()}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toast({ title: 'QR Code downloaded!' });
        } catch (err) {
            toast({ title: 'Failed to download', variant: 'destructive' });
        }
    }, [qrUrl, toast]);

    const displayText = text.length > 100 ? text.substring(0, 100) + '...' : text;

    return (
        <Card
            className={`overflow-hidden w-full max-w-md border rounded-2xl transition-colors duration-300 ${isDarkMode
                ? 'bg-[#1a1a1a] border-[#2d2d2d]'
                : 'bg-white border-gray-300'
                }`}
        >

            {/* Content */}
            <div className="p-4 space-y-5">

                {/* QR Code Display */}
                <div className={`flex flex-col items-center rounded-xl p-0 bg-transparent transition-colors ${isDarkMode
                    ? ''
                    : ''
                    }`}>
                    <div className={`relative rounded-xl overflow-hidden bg-white p-3 shadow-lg ${!imageLoaded ? 'animate-pulse' : ''
                        }`}>
                        <img
                            src={qrUrl}
                            alt="QR Code"
                            className="w-44 h-44"
                            onLoad={() => setImageLoaded(true)}
                        />
                        {!imageLoaded && (
                            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                                <div className="w-8 h-8 border-2 border-violet-500 border-t-transparent rounded-full animate-spin" />
                            </div>
                        )}
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                    <button
                        onClick={handleDownload}
                        className={`flex-1 py-3 px-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 transition-all active:scale-[0.98] shadow-lg ${isDarkMode
                            ? 'bg-violet-600 hover:bg-violet-500 shadow-violet-900/30'
                            : 'bg-violet-600 hover:bg-violet-700 shadow-violet-200'
                            }`}
                    >
                        <Download className="w-4 h-4" />
                        Download QR
                    </button>
                </div>
            </div>
        </Card>
    );
};

export default QRCodeGeneratorCard;
